import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import HabitTracker from "@/components/HabitTracker";
import { useAuth } from "@/context/AuthContext";
import { SupabaseHabitProvider, useHabits } from "@/context/SupabaseHabitContext";
import { MoodTrackerProvider } from "@/context/MoodTrackerContext";
import { Skeleton } from "@/components/ui/skeleton";
import CombinedHabitGrid from "@/components/habit-grid/CombinedHabitGrid";
import { useIsMobile } from "@/hooks/use-mobile";
import DailyChecklist from "@/components/DailyChecklist";
import TopBar from "@/components/TopBar";
import PinnedHabits from "@/components/PinnedHabits";
import DashboardToolbar from "@/components/DashboardToolbar";
import MoodTrackerWithAnalysis from "@/components/MoodTrackerWithAnalysis";
import { toast } from "sonner";
import { TimezoneMigrationAlert } from "@/components/TimezoneMigrationAlert";
import OnboardingModal from "@/components/OnboardingModal";
import UpgradeDialog from "@/components/pricing/UpgradeDialog";

function DashboardContent() {
  const isMobile = useIsMobile();
  const [selectedDate, setSelectedDate] = useState(new Date());

  // Show/hide paused habits state with localStorage persistence
  const [showPausedHabits, setShowPausedHabits] = useState<boolean>(() => {
    const saved = localStorage.getItem('showPausedHabits');
    return saved ? JSON.parse(saved) : false; // Default to false (hide paused habits)
  });

  // Save to localStorage whenever showPausedHabits changes
  useEffect(() => {
    localStorage.setItem('showPausedHabits', JSON.stringify(showPausedHabits));
  }, [showPausedHabits]);

  const handleToggleShowPausedHabits = (show: boolean) => {
    setShowPausedHabits(show);
  };

  return (
    <div className="container max-w-4xl">
      <div className="space-y-8">
        <MoodTrackerProvider>
          <TopBar showPausedHabits={showPausedHabits} selectedDate={selectedDate} />
          <DailyChecklist 
            showPausedHabits={showPausedHabits} 
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            onToggleShowPausedHabits={handleToggleShowPausedHabits}
          />
          <CombinedHabitGrid initialMonths={isMobile ? 4 : 8} />
          <PinnedHabits />
          <MoodTrackerWithAnalysis />
          <HabitTracker showPausedHabits={showPausedHabits} />
          
          {/* Bottom Toolbar */}
          <DashboardToolbar 
            showPausedHabits={showPausedHabits}
            onToggleShowPausedHabits={handleToggleShowPausedHabits}
          />
        </MoodTrackerProvider>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, loading, isPremium, onboarded, markOnboarded } = useAuth();
  const [searchParams] = useSearchParams();
  const isMobile = useIsMobile();
  const openOnboarding = !!user && onboarded === false;
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  
  useEffect(() => {
    document.title = "Dashboard | Habitualy";
    console.log("Dashboard mounted, auth state:", { user: user?.id, loading });

    // Check for successful Stripe payment
    const sessionId = searchParams.get('session_id');
    if (sessionId && user?.id) {
      console.log('Found session ID in URL:', sessionId);
      
      const verifyPayment = async () => {
        try {
          console.log('Verifying payment with session ID:', sessionId);
          const verifyUrl = `${window.location.origin}/api/verify-session?session_id=${sessionId}&user_id=${user.id}`;
          
          console.log('Calling verify endpoint:', verifyUrl);
          const response = await fetch(verifyUrl);
          
          if (!response.ok) {
            const errorData = await response.json();
            console.error('Error verifying payment:', errorData);
            throw new Error(errorData.error || 'Failed to verify payment');
          }
          
          const result = await response.json();
          console.log('Payment verification result:', result);
          
          // Remove the query parameter BEFORE doing anything else
          searchParams.delete('session_id');
          const newUrl = window.location.pathname;
          window.history.replaceState({}, '', newUrl);
          
          if (result.success) {
            toast.success(
              <div className="flex flex-col gap-1">
                <p className="font-medium">Welcome to Premium! 🎉</p>
                <p className="text-sm">You now have access to all premium features.</p>
              </div>
            );
            // Now safe to reload since we've removed the parameter
            window.location.reload();
          } else {
            toast.error('Unable to verify premium status. Please refresh the page or contact support.');
          }
        } catch (error) {
          console.error('Error verifying payment:', error);
          toast.error('Unable to verify payment status. Please contact support if your premium features are not available.');
          
          // Make sure to clean up the URL even if there's an error
          searchParams.delete('session_id');
          window.history.replaceState({}, '', window.location.pathname);
        }
      };
      
      verifyPayment();
    } else if (sessionId) {
      // Clean up URL if session ID exists but no user
      searchParams.delete('session_id');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [user, loading, searchParams]);

  // Show upgrade dialog for free users who are already onboarded
  useEffect(() => {
    if (user && !isPremium && onboarded) {
      // Small delay to ensure the page has loaded and onboarding isn't showing
      const timer = setTimeout(() => {
        setUpgradeDialogOpen(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [user, isPremium, onboarded]);

  // If not logged in and finished loading, redirect to auth page
  if (!loading && !user) {
    console.log("Not logged in, redirecting to auth");
    return <Navigate to="/auth" replace />;
  }
  
  // Show loading state while checking auth
  if (loading) {
    console.log("Dashboard loading...");
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-1 py-8">
          <div className="container max-w-4xl">
            <div className="space-y-4">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-[200px]" />
              <Skeleton className="h-[200px]" />
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  console.log("Dashboard rendering with user:", user?.id);
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 py-8">
        <SupabaseHabitProvider>
          <DashboardContent />
          <TimezoneMigrationAlert />
        </SupabaseHabitProvider>
        <OnboardingModal
          open={openOnboarding}
          onComplete={markOnboarded}
          isPremium={isPremium}
        />
        <UpgradeDialog
          open={upgradeDialogOpen}
          onOpenChange={setUpgradeDialogOpen}
        />
      </main>
      <Footer />
    </div>
  );
}
